import * as THREE from "three";

export const TRAIL_COLOURS: Record<string, number> = { "cos.trail.crimson": 0xef4444, "cos.trail.cyan": 0x22d3ee, "cos.trail.gold": 0xf5c542 };
const SEGMENTS = 14, MARKS = 9;

/** Local, bounded geometry shared by the real march and its store preview (three draws). */
export class MarchTrail {
  readonly group = new THREE.Group();
  private readonly ribbon = new THREE.BufferGeometry();
  private readonly core = new THREE.BufferGeometry();
  private readonly chevron = new THREE.BufferGeometry().setAttribute("position", new THREE.Float32BufferAttribute([-0.16, 0, -0.12, 0, 0, 0.12, 0, 0, 0.02, 0, 0, 0.02, 0, 0, 0.12, 0.16, 0, -0.12], 3));
  private readonly ring = new THREE.TorusGeometry(0.1, 0.018, 5, 16).rotateX(Math.PI / 2);
  private readonly crystal = new THREE.OctahedronGeometry(0.11).scale(1, 0.4, 1);
  private readonly ribbonMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.26, depthWrite: false, side: THREE.DoubleSide });
  private readonly coreMat = new THREE.MeshBasicMaterial({ color: 0xffedc9, transparent: true, opacity: 0.7, depthWrite: false, side: THREE.DoubleSide });
  private readonly markMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.85, depthWrite: false, side: THREE.DoubleSide });
  private readonly marks = new THREE.InstancedMesh(this.chevron, this.markMat, MARKS);
  private readonly matrix = new THREE.Matrix4();
  private disposed = false;
  private cosmetic: string | null = null;

  constructor(cosmetic: string | null = null) {
    this.group.name = "march-cosmetic-trail";
    for (const geometry of [this.ribbon, this.core]) geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(SEGMENTS * 18), 3).setUsage(THREE.DynamicDrawUsage));
    for (const material of [this.ribbonMat, this.coreMat, this.markMat]) material.forceSinglePass = true;
    this.group.add(new THREE.Mesh(this.ribbon, this.ribbonMat), new THREE.Mesh(this.core, this.coreMat), this.marks);
    this.group.children.forEach((child) => { child.frustumCulled = false; child.renderOrder = 5; });
    this.setCosmetic(cosmetic);
  }

  setCosmetic(id: string | null): void {
    if (this.disposed) return;
    this.cosmetic = id && Object.hasOwn(TRAIL_COLOURS, id) ? id : null;
    this.group.userData["cosmetic"] = this.cosmetic;
    this.ribbonMat.color.setHex(TRAIL_COLOURS[this.cosmetic ?? ""] ?? 0xffffff);
    this.markMat.color.copy(this.ribbonMat.color);
    this.marks.geometry = id === "cos.trail.cyan" ? this.ring : id === "cos.trail.gold" ? this.crystal : this.chevron;
    this.group.visible = !!this.cosmetic;
  }

  update(from: THREE.Vector3, to: THREE.Vector3, progress: number, reduced = false): void {
    if (this.disposed) return;
    const distance = Math.hypot(to.x - from.x, to.z - from.z), t = THREE.MathUtils.clamp(progress, 0, 1);
    const length = Math.min(3.6, distance * t);
    this.group.visible = !!this.cosmetic && length > 0.03 && Number.isFinite(length);
    if (!this.group.visible) return;
    this.group.position.copy(from).lerp(to, t); this.group.position.y = Math.max(from.y, to.y) + 0.23;
    this.group.rotation.y = Math.atan2(to.x - from.x, to.z - from.z);
    for (const [geometry, width] of [[this.ribbon, 0.16], [this.core, 0.021]] as const) {
      const positions = geometry.getAttribute("position") as THREE.BufferAttribute;
      for (let i = 0; i < SEGMENTS; i++) {
        const a = i / SEGMENTS, b = (i + 1) / SEGMENTS;
        const wa = width * (1 - a) ** 0.55, wb = width * (1 - b) ** 0.55;
        const y = geometry === this.core ? 0.009 : 0;
        const points = [[-wa, y, -a * length], [wa, y, -a * length], [-wb, y, -b * length], [wa, y, -a * length], [wb, y, -b * length], [-wb, y, -b * length]];
        points.forEach((p, n) => positions.setXYZ(i * 6 + n, p[0]!, p[1]!, p[2]!));
      }
      positions.needsUpdate = true;
    }
    for (let i = 0; i < MARKS; i++) {
      const fraction = (i + 0.5) / MARKS;
      const scale = (1 - fraction * 0.65) * (reduced ? 1 : 1 + Math.sin(t * 40 - i) * 0.08);
      this.matrix.makeScale(scale, scale, scale).setPosition(0, 0.025, -fraction * length);
      this.marks.setMatrixAt(i, this.matrix);
    }
    this.marks.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.group.removeFromParent(); this.group.clear();
    for (const geometry of [this.ribbon, this.core, this.chevron, this.ring, this.crystal]) geometry.dispose();
    for (const material of [this.ribbonMat, this.coreMat, this.markMat]) material.dispose();
    this.marks.dispose();
  }
}
